/**
 * Derivations for the quick-write document surfaces.
 *
 * Both the panel header ("Draft · 412 words") and the chat chip's page sketch
 * are read straight off the Markdown the message already carries — nothing here
 * fetches, and nothing here is stored.
 */

/** Kinds of line the chip's page sketch can draw. */
export type SketchLineKind = 'heading' | 'text' | 'list'

export interface SketchLine {
  kind: SketchLineKind
  /** Short source lines draw a narrower bar, so the sketch keeps the document's shape. */
  short: boolean
}

/** Beyond this the sketch is taller than the chip, so the rest is never drawn. */
const DEFAULT_SKETCH_LINES = 9

/** Source lines under this width read as short and draw a narrower bar. */
const SHORT_LINE_CHARS = 42

/**
 * Markdown syntax that carries no words: fence markers, list bullets, heading
 * hashes, blockquote arrows, table pipes, emphasis and link/image brackets.
 */
function toPlainText(markdown: string): string {
  return markdown
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/`[^`]*`/g, ' ')
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/^\s{0,3}#{1,6}\s+/gm, '')
    .replace(/^\s{0,3}>\s?/gm, '')
    .replace(/^\s*([*+-]|\d+[.)])\s+/gm, '')
    .replace(/[*_~]/g, '')
    .replace(/\|/g, ' ')
    .replace(/^\s*[-:| ]+\s*$/gm, ' ')
}

/**
 * Words in the document, as a reader would count them. Markdown syntax is
 * stripped first so `## Governance` counts as one word, not two.
 */
export function countWords(markdown: string): number {
  const text = toPlainText(markdown).trim()
  if (!text) return 0
  return text.split(/\s+/).length
}

/**
 * A structural sketch of the document's opening — one entry per rendered line,
 * used to draw the miniature page on the chat chip. Blank lines, code fences and
 * table rules are skipped so the sketch reflects what the reader would see.
 */
export function sketchDocument(markdown: string, maxLines = DEFAULT_SKETCH_LINES): SketchLine[] {
  const lines: SketchLine[] = []

  for (const raw of markdown.split('\n')) {
    if (lines.length >= maxLines) break

    const line = raw.trim()
    if (!line) continue
    // Fences and table rules render as nothing a reader would count as a line.
    if (line.startsWith('```') || /^\|?[-:| ]+\|?$/.test(line)) continue

    if (/^#{1,6}\s+/.test(line)) {
      lines.push({ kind: 'heading', short: true })
      continue
    }

    const isList = /^([*+-]|\d+[.)])\s+/.test(line)
    const body = line.replace(/^([*+-]|\d+[.)])\s+/, '')
    lines.push({ kind: isList ? 'list' : 'text', short: body.length < SHORT_LINE_CHARS })
  }

  return lines
}
